import { useEffect, useRef, useState } from 'react';
import useCallApiAndLoad from '../../../hooks/useCallApiAndLoad';
import useAuth from '../../../hooks/useAuth';
import { fetchResponseAdapter } from '../../../adapters/fetchAdapter';
import { getMonthlyPreSaleReportEndpoint } from '../services/dashboardService';
import { getDevelopersEndpoint } from '../../investors/services/developersService';

export interface MonthlyReport {
  month: string;
  developer: { id: string; name: string };
  generated_at: string;
  ai_generated: boolean;
  // generated | not_enough_data | failed
  ai_status?: 'generated' | 'not_enough_data' | 'failed';
  executive_summary: string | null;
  totals: {
    visits: number;
    previous_month_visits: number;
    delta: number;
    delta_percentage: number | null;
  };
  top_model: { name: string; percentage: number } | null;
  interest: {
    hot_clients_change_percentage: number | null;
    trend: { month: string; hot_clients_percentage: number | null }[];
    trend_summary: string | null;
  };
  objections: { key: string; percentage: number }[];
  competitors: {
    items: { name: string; percentage: number }[];
    summary: string | null;
  };
  funnel: { key: string; percentage: number }[];
  recommendation: string | null;
}

export interface DeveloperOption {
  id: string;
  name: string;
}

export type ReportState =
  | 'idle'
  | 'loading'
  | 'ready'
  | 'no-data'
  | 'no-developer'
  | 'error';

// Códigos de 404 del reporte: distinguirlos por `code`, no por el status.
const NO_DATA_CODE = 1120;
const NO_DEVELOPER_CODE = 1121;

const useMonthlyPreSaleReport = (month: string, developerId: string) => {
  const { callEndpoint } = useCallApiAndLoad();
  const { getAuthToken } = useAuth();
  const token = getAuthToken();
  const [developers, setDevelopers] = useState<DeveloperOption[]>([]);
  const [state, setState] = useState<ReportState>('idle');
  const [report, setReport] = useState<MonthlyReport | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const refreshRequested = useRef(false);

  useEffect(() => {
    callEndpoint(getDevelopersEndpoint(token))
      .then(({ status, data }) => {
        if (status === 200) setDevelopers(fetchResponseAdapter(data).data || []);
      })
      .catch(() => setDevelopers([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!month || !developerId) {
      setState('idle');
      return undefined;
    }
    let active = true;
    setState('loading');
    setReport(null);
    const refresh = refreshRequested.current;
    refreshRequested.current = false;
    (async () => {
      const { status, data } = await callEndpoint(
        getMonthlyPreSaleReportEndpoint(month, developerId, token, refresh),
      );
      if (!active) return;
      if (status === 200) {
        setReport(fetchResponseAdapter(data).data);
        setState('ready');
        return;
      }
      const code = fetchResponseAdapter(data ?? {}).code;
      if (status === 404 && code === NO_DATA_CODE) setState('no-data');
      else if (status === 404 && code === NO_DEVELOPER_CODE)
        setState('no-developer');
      else setState('error');
    })();
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month, developerId, refreshKey]);

  // Pide textos nuevos de IA (sólo cuando el usuario lo solicita).
  const regenerate = () => {
    refreshRequested.current = true;
    setRefreshKey((key) => key + 1);
  };

  return { developers, state, report, regenerate };
};

export default useMonthlyPreSaleReport;
